import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  DomainsState,
  Domain,
  CreateDomainDto,
  UpdateDomainDto,
} from "@/types/models/domain";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

const initialState: DomainsState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    totalPages: 1,
    pageSize: 10,
    total: 0,
  },
  formState: {
    loading: false,
    error: null,
    success: false,
  },
};

const domainSlice = createSlice({
  name: "domains",
  initialState,
  reducers: {
    // Fetch domains
    fetchDomainsRequest: (
      state,
      _action: PayloadAction<{ page?: number; limit?: number }>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchDomainsSuccess: (
      state,
      action: PayloadAction<{ data: Domain[]; pagination: any }>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.items = action.payload.data;
      state.pagination = {
        page: action.payload.pagination?.page || 1,
        totalPages: action.payload.pagination?.totalPages || 1,
        pageSize: action.payload.pagination?.pageSize || 10,
        total: action.payload.pagination?.total || action.payload.data.length,
      };
      state.error = null;
    },
    fetchDomainsFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.loaded = true;
      state.error = action.payload;
    },

    // Create domain
    createDomainRequest: (state, _action: PayloadAction<CreateDomainDto>) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createDomainSuccess: (state, action: PayloadAction<Domain>) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items.unshift(action.payload);
      state.pagination.total += 1;
    },
    createDomainFailure: (state, action: PayloadAction<any>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Update domain
    updateDomainRequest: (
      state,
      _action: PayloadAction<{ id: string; data: UpdateDomainDto }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateDomainSuccess: (state, action: PayloadAction<Domain>) => {
      state.formState.loading = false;
      state.formState.success = true;
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    updateDomainFailure: (state, action: PayloadAction<any>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Delete domain
    deleteDomainRequest: (state, _action: PayloadAction<{ id: string }>) => {
      state.loading = true;
      state.error = null;
    },
    deleteDomainSuccess: (state, action: PayloadAction<{ id: string }>) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload.id);
      state.pagination.total = Math.max(0, state.pagination.total - 1);
    },
    deleteDomainFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Reset form state
    resetFormState: (state) => {
      state.formState = {
        loading: false,
        error: null,
        success: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchDomainsRequest,
  fetchDomainsSuccess,
  fetchDomainsFailure,
  createDomainRequest,
  createDomainSuccess,
  createDomainFailure,
  updateDomainRequest,
  updateDomainSuccess,
  updateDomainFailure,
  deleteDomainRequest,
  deleteDomainSuccess,
  deleteDomainFailure,
  resetFormState,
} = domainSlice.actions;

export default domainSlice.reducer;
